// ./src/components/features/PlaceLocationSection/PlaceLocationSection.tsx

import React from "react";
import {
  Stack,
  Group,
  Text,
  Switch,
} from "@mantine/core";
import { useTranslation } from "react-i18next";

import { UsePlaceLocationReturn } from "@/hooks/usePlaceLocation";

import {
  AppIcon,
  PlaceAutocompleteInput,
  AddressAutocompleteInput,
  LocationPreview,
} from "@/components/generic";
import { PlaceTypeSelector } from "@/components/features";

// ============================================
// Types
// ============================================

export interface PlaceLocationSectionProps {
  /** Return value from usePlaceLocation hook */
  placeLocation: UsePlaceLocationReturn;

  /**
   * Show the PlaceTypeSelector (placeType).
   * Enable for travel-related drawers (start travel, waypoint).
   * @default false
   */
  showPointType?: boolean;

  /**
   * Show the place name input (whereDone / PlaceAutocompleteInput).
   * @default true
   */
  showPlaceName?: boolean;

  /**
   * Show the address input (location / AddressAutocompleteInput).
   * @default true
   */
  showAddress?: boolean;

  /**
   * Show the location preview map when coordinates are available.
   * @default true
   */
  showLocationPreview?: boolean;

  /**
   * Show the "Save this place" toggle.
   * @default true
   */
  showSaveToggle?: boolean;

  /**
   * Show the locate button on the address input.
   * @default true
   */
  showLocateButton?: boolean;

  /**
   * Height of the map preview in pixels.
   * @default 120
   */
  mapHeight?: number;

  /**
   * Zoom level for the map preview.
   * @default 15
   */
  mapZoom?: number;

  /**
   * Place types filter for PlaceAutocompleteInput.
   * Empty array allows all place types.
   * @default []
   */
  placeTypes?: string[];

  /**
   * Field-specific errors from the parent drawer's ApiError parsing.
   * Supports keys: placeType, whereDone, location, savedPlaceId, savePlace
   */
  fieldErrors?: Record<string, string | undefined>;

  // ── Label/placeholder overrides ──

  /** Override label for the place type selector */
  pointTypeLabel?: string;

  /** Override label for the place name input */
  placeNameLabel?: string;

  /** Override placeholder for the place name input */
  placeNamePlaceholder?: string;

  /** Override label for the address input */
  addressLabel?: string;

  /** Override placeholder for the address input */
  addressPlaceholder?: string;

  /** Override label for the save place toggle */
  savePlaceLabel?: string;

  /** Override description for the save place toggle */
  savePlaceDescription?: string;

  /**
   * Whether the place type selector is required.
   * @default false
   */
  placeTypeRequired?: boolean;

  /**
   * Gap between child elements.
   * @default "md"
   */
  gap?: string;
}

// ============================================
// Component
// ============================================

const PlaceLocationSectionComponent: React.FC<PlaceLocationSectionProps> = ({
  placeLocation,
  showPointType = false,
  showPlaceName = true,
  showAddress = true,
  showLocationPreview = true,
  showSaveToggle = true,
  showLocateButton = true,
  mapHeight = 120,
  mapZoom = 15,
  placeTypes = [],
  fieldErrors = {},
  pointTypeLabel,
  placeNameLabel,
  placeNamePlaceholder,
  addressLabel,
  addressPlaceholder,
  savePlaceLabel,
  savePlaceDescription,
  placeTypeRequired = false,
  gap = "md",
}) => {
  const { t } = useTranslation(["savedPlaces", "common"]);

  const {
    // Values
    whereDone,
    location,
    latitude,
    longitude,
    pointType,
    savePlace,

    // Autocomplete support
    locationBias,
    savedPlacesAsPlaceData,

    // Status
    isLocationLoading,
    canSavePlace,

    // Handlers
    handleWhereDoneChange,
    handleLocationChange,
    handleSavePlaceChange,
    handlePointTypeChange,
  } = placeLocation;

  return (
    <Stack gap={gap}>
      {/* Place Name (whereDone) — Gas station, office, home, etc. */}
      {showPlaceName && (
        <PlaceAutocompleteInput
          value={whereDone}
          onChange={handleWhereDoneChange}
          recentPlaces={savedPlacesAsPlaceData}
          locationBias={locationBias}
          placeTypes={placeTypes}
          label={placeNameLabel || t("common:fields.placeName.label")}
          placeholder={placeNamePlaceholder || t("common:fields.placeName.placeholder")}
          error={fieldErrors.whereDone}
          size="md"
          withinPortal
        />
      )}

      {/* Full Address (location) */}
      {showAddress && (
        <AddressAutocompleteInput
          value={location}
          onChange={handleLocationChange}
          locationBias={locationBias}
          label={addressLabel || t("common:fields.address.label")}
          placeholder={addressPlaceholder || t("common:fields.address.placeholder")}
          error={fieldErrors.location}
          size="md"
          showLocateButton={showLocateButton}
          locateButtonTooltip={t("common:buttons.useMyLocation")}
          withinPortal
        />
      )}

      {/* Location status: detecting indicator */}
      {isLocationLoading && (
        <Group gap="xs" ml="xs">
          <AppIcon
            iconName="MapPin"
            size={14}
            color="var(--mantine-color-blue-6)"
          />
          <Text size="xs" c="dimmed">
            {t("common:location.detecting")}
          </Text>
        </Group>
      )}

      {/* Location preview with map and coordinates */}
      {showLocationPreview && !isLocationLoading && latitude != null && longitude != null && (
        <LocationPreview
          latitude={latitude}
          longitude={longitude}
          showMap
          mapHeight={mapHeight}
          showCoordinates
          zoom={mapZoom}
        />
      )}

      {/* Place Type Selector (optional) */}
      {showPointType && (
        <PlaceTypeSelector
          name="pointType"
          value={pointType}
          onChange={handlePointTypeChange}
          required={placeTypeRequired}
          label={pointTypeLabel}
          error={fieldErrors.placeType}
        />
      )}

      {/* Save this place toggle — only shown when location is entered manually */}
      {showSaveToggle && canSavePlace && (
        <Switch
          label={savePlaceLabel || t("common:fields.savePlace.label")}
          description={savePlaceDescription || t("common:fields.savePlace.description")}
          checked={savePlace}
          onChange={handleSavePlaceChange}
          size="md"
        />
      )}
    </Stack>
  );
};

export const PlaceLocationSection = React.memo(PlaceLocationSectionComponent);